import {
	eligibilityPayorsForTenant,
	payorLabelsForStaff,
} from "@repo/config/tenants";
import { Button, Flex } from "@repo/ui";
import { useMemo } from "react";
import { MarkdownDocView } from "../components/MarkdownDocView";
import { parseMarkdown } from "../markdown/parser";
import { getTenantSlug } from "../tenant";

/**
 * The supported-plan list staff read, derived from the tenant's payor registry
 * rather than hand-maintained. The hand-written version had drifted to four
 * names, none of which matched a spelling any code path recognised.
 *
 * Named exactly as the insurance-modal dropdown does, since that is where a
 * reader goes to act on it.
 */
function supportedPlansMarkdown(): string {
	const tenantSlug = getTenantSlug();
	if (!tenantSlug) return "";
	return payorLabelsForStaff(eligibilityPayorsForTenant(tenantSlug))
		.map((label) => `- ${label}`)
		.join("\n");
}

/**
 * Built per render rather than once at module load. `getTenantSlug()` reads the
 * URL, and in development the slug is the first path segment — so evaluating
 * this at import time, which a static import in `App.tsx` does at app boot,
 * froze an empty plan list for any session that started outside a tenant path.
 */
function trainingGuideContent(): string {
	return `# Referral Workflow

This document describes how Skyway processes specialist referrals from start to finish.

## 1. Pulling Referrals

Skyway automatically pulls referrals from your EHR twice daily: once in the morning and once in the afternoon. During each sync, the system logs into your EHR, navigates to the referrals section, and retrieves all open referrals.
For each referral, Skyway extracts:

- Patient name, date of birth, phone number, and address
- Insurance information (payor, member ID, plan type)
- Referral date, specialty, and priority
- Diagnosis codes
- Doctor notes and comments (this is where provider preferences are typically documented)

When referrals are pulled in, they are marked as Action Required.

You can also trigger a manual sync at any time if you need referrals pulled outside the scheduled windows. You can do this by filtering for a specific department and clicking the "Sync now" button.

![Manual sync button](/images/training-guide/manual-sync.png)

### Troubleshooting Pull Issues

If Skyway encounters issues while pulling referrals, support is likely already investigating. New referrals won't be synced until the issue is resolved, but once it is, Skyway will backfill any missed referrals automatically.

## 2. The Skyway Agent

The Skyway Agent (affectionately known as Chad) is an AI-powered assistant that processes each referral end-to-end. It performs tasks such as checking insurance eligibility, finding providers, submitting authorizations, sending referrals, and messaging patients.

Once a referral is pulled in, you can assign it to the Skyway Agent by clicking "Assign to agent" on the task page.

![Assign to agent button](/images/training-guide/assign-to-agent.png)

After assignment, the agent begins working on the referral immediately. You can monitor its progress in the activity log, which records each step taken along with timestamps, screenshots, and any errors encountered.

![Agent processing](/images/training-guide/agent-processing.png)

If there is an error in any of the steps, it'll appear back in your inbox. You can then review the activity log to understand what went wrong, make any necessary corrections, and reassign the task to the agent to continue processing.

## 3. Eligibility Check

![Insurance eligibility check results](/images/training-guide/eligibility-check.png)

After pulling a referral, the Skyway Agent checks the patient's insurance eligibility through the appropriate portal (Availity for most payors, UHC portal for UnitedHealthcare plans).

The eligibility check determines:

- Whether the patient's insurance is active
- The plan type (PPO, HMO, EPO)
- The primary care provider (PCP) registered with the insurance
- Whether specialist referrals require prior authorization

PPO plans typically do not require prior authorization, while HMO plans typically do. This check determines whether the workflow proceeds to the referral authorization step.

### PCP changes

If the eligibility check shows that the patient's PCP needs to be changed to a provider within your organization (which is needed to submit a prior authorization), Skyway messages the patient with instructions to call their insurance and request the change. After the patient confirms the PCP change, Skyway re-runs the eligibility check (up to 3 times over several days) to allow time for the insurance system to update.

### Supported Insurance Plans

Not every insurance plan is supported for eligibility checks. If Skyway cannot perform the check, it shows an error in the activity log. In this case, staff can either manually verify eligibility by calling the insurance plan or choose to skip the eligibility check and authorization steps entirely if they know none is required. These are the supported insurance plans for eligibility checks:

${supportedPlansMarkdown()}

## 4. Finding a Provider

Once the agent runs an eligibility check, it looks for an appropriate specialist provider based on the referral details and any preferences documented by the referring doctor.

### Provider database

Skyway maintains a database of commonly used providers. Each provider record includes:

- Provider name and NPI
- Organization and specialty
- Location, phone number, and fax number
- Insurance acceptance information
- Business hours
- Telehealth availability

You can search this database directly from the task page at the bottom of the activity log. Skyway's AI agent also uses this same database when processing referrals to find appropriate specialists.

![Provider search interface](/images/training-guide/provider-search.png)

### Sending to a specific provider

If you want the agent to send to a specific provider, you can add a comment to the referral before assigning it to the agent. For example, you might write "Please send to Dr. Smith at Main St. Clinic." The agent will prioritize that provider when processing the referral and reassign the task back to staff if the provider cannot be found or does not accept the patient's insurance.

## 5. Phone Calls

When provider information is incomplete or needs verification, Skyway makes AI phone calls to provider offices. The phone agent:

1. Introduces itself as calling from a provider's office
2. Asks for the fax number (if not already known)
3. Confirms whether they accept the patient's insurance
4. Asks about appointment availability
5. If prior authorization is needed, requests the provider name and NPI for the authorization form

Phone calls are placed during business hours (9 AM to 5 PM in the destination timezone, weekdays only). If the agent attempts a call outside these hours, it schedules a follow-up for the next business day.

Call recordings and transcripts are saved and you can click on them to see what was talked about.

![Phone call transcript example](/images/training-guide/call-transcript.png)

### Skipping phone calls

Phone calls are not always successful, so if you prefer that Skyway not make phone calls for a specific referral and you know the fax number to send to, you can add a comment saying "Fax number is _, do not call". If the agent needs provider information but you've requested not to call, it will reassign it back to staff.

## 6. Referral Authorization

Once the provider to send to has been identified, Skyway submits a referral authorization request if required by the patient's insurance plan.

![Authorization submission form](/images/training-guide/referral-auth.png)

On success, Skyway captures the authorization code, number of visits approved, and valid date range. This authorization code will be attached to the referral when it is faxed to the specialist.

## 7. Sending the Referral

Once authorization is obtained (or if none is required), Skyway sends the referral through the EHR's fax system.

![Referral fax submission](/images/training-guide/send-referral.png)

The process:

1. Opens the referral in the EHR
2. Fills in authorization details (if applicable): authorization number, start/end dates, approved visits
3. Enters recipient provider information: name, address, fax number
4. Attaches relevant documents from the patient's chart (lab results, clinical notes, imaging reports)
5. Selects fax as the transmission method
6. Submits and verifies confirmation
7. Adds a comment in the EHR referral indicating the referral was sent, including the provider name, address, and phone number.

Document attachment (step 4) is intelligent: Skyway evaluates each document in the patient's chart for relevance to the referral, automatically excluding documents older than 5 years.

### Resending Referrals

Sometimes providers request that a referral be resent. For referrals pulled from Azalea, you can just ask the agent to resend it.

For the Athena EHR, you currently need to:

1. Go into the EHR and mark the referral as "Submit".
2. Sync the referrals so Skyway pulls it back in.
3. Tell the agent to resend the referral.

## 8. Patient Messages

The Skyway Agent can send messages to patients through Spruce. Communicating with patients through Skyway first requires consent.

![Patient messaging interface](/images/training-guide/sent-message.png)

### Consent Flow

When Skyway needs to message a patient for the first time, it sends a consent request:
"Hi, this is [Clinic Name]. To verify we're texting the right person, please reply with your date of birth (MM/DD/YYYY)."

Once the patient replies with a date of birth matching their record, consent is granted. If the patient replies with "stop" or a wrong date of birth, consent is declined and Skyway will not message them further.

If the patient does not respond, Skyway sends up to 2 consent requests before escalating to staff.

You can also manually update the consent status from the task page by clicking "SMS consent" on the patient card in the referral and selecting Consented or Declined. This will let the agent message them directly.

![Update consent button](/images/training-guide/manual-consent.png)

### Notification Types

Once consent is obtained, Skyway sends notifications at key points:

- Provider preference requests: Asks if the patient has any preferences for a specific doctor or location
- PCP change instructions: When the patient needs to change their PCP, provides the information needed to call their insurance
- Referral sent confirmation: Notifies the patient when the referral has been faxed, including the provider's name, address, and phone number

Messages are automatically translated to Spanish if that is the patient's primary language.

---

## Appendix: How Integrations Work

Skyway uses browser automation to interact with EHRs and insurance portals. This means the system logs in and navigates the interface the same way you would.
If something goes wrong during automation, Skyway captures:

- A screenshot of the screen at the time of failure
- A snapshot of the page content
- The error message

These artifacts help diagnose issues and are available in the task's activity log.
`;
}

export function TrainingGuideRoute() {
	const doc = useMemo(() => parseMarkdown(trainingGuideContent()), []);
	return (
		<Flex column flex overflow="y" background="sand2" padding="xl" gap="lg">
			<Flex align="center" gap="md">
				<Button
					variant="ghost"
					size="md"
					icon="ChevronLeft"
					onPress={() => window.history.back()}
				>
					Back
				</Button>
			</Flex>

			<Flex
				column
				gap="lg"
				style={{ maxWidth: 800, margin: "0 auto", width: "100%" }}
			>
				<MarkdownDocView doc={doc} size="prose" />
			</Flex>
		</Flex>
	);
}
